import { useState, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useToast } from '@/hooks/use-toast';
import { showErrorToast } from '@/lib/api';
import {
  Upload,
  FileText,
  Image,
  FileSpreadsheet,
  File,
  Download,
  Trash2,
  Loader2,
} from 'lucide-react';
import type { AMLFormDocument, DocumentType } from '@/types/aml-forms';

const ALLOWED_EXTENSIONS = [
  'pdf', 'doc', 'docx', 'xls', 'xlsx', 'png', 'jpg', 'jpeg', 
  'gif', 'txt', 'csv', 'odt', 'ods', 'rtf', 'zip', 'rar', '7z'
];

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

const DOCUMENT_TYPE_LABELS: Record<DocumentType, string> = {
  identity: 'Identity Document',
  address_proof: 'Proof of Address',
  company_registration: 'Company Registration',
  financial: 'Financial Document',
  contract: 'Contract',
  other: 'Other',
};

const DOCUMENT_TYPE_COLORS: Record<DocumentType, string> = {
  identity: 'bg-blue-100 text-blue-800 border-blue-200',
  address_proof: 'bg-green-100 text-green-800 border-green-200',
  company_registration: 'bg-purple-100 text-purple-800 border-purple-200',
  financial: 'bg-amber-100 text-amber-800 border-amber-200',
  contract: 'bg-pink-100 text-pink-800 border-pink-200',
  other: 'bg-muted text-foreground border-border',
};

interface DocumentUploadProps {
  documents: AMLFormDocument[];
  onUpload: (file: File, documentType?: DocumentType, description?: string, fieldId?: string) => Promise<void>;
  onDownload: (document: AMLFormDocument) => Promise<void>;
  onDelete: (document: AMLFormDocument) => Promise<void>;
  disabled?: boolean;
  fieldId?: string;
  suggestedDocumentType?: DocumentType;
  compact?: boolean;
  readOnly?: boolean;
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

function getFileIcon(mimeType: string) {
  if (mimeType.startsWith('image/')) {
    return <Image className="w-4 h-4 text-blue-500" />;
  }
  if (mimeType.includes('pdf')) {
    return <FileText className="w-4 h-4 text-red-500" />;
  }
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel') || mimeType.includes('csv')) {
    return <FileSpreadsheet className="w-4 h-4 text-green-500" />;
  }
  return <File className="w-4 h-4 text-muted-foreground" />;
}

function getFileExtension(filename: string): string {
  return filename.split('.').pop()?.toLowerCase() || '';
}

export function DocumentUpload({
  documents,
  onUpload,
  onDownload,
  onDelete,
  disabled = false,
  fieldId,
  suggestedDocumentType,
  compact = false,
  readOnly = false,
}: DocumentUploadProps) {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [documentType, setDocumentType] = useState<DocumentType>(suggestedDocumentType || 'other');
  const [description, setDescription] = useState('');
  const [deleteDocument, setDeleteDocument] = useState<AMLFormDocument | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  // Filter documents for this field if fieldId is provided
  const filteredDocuments = fieldId 
    ? documents.filter(doc => doc.field_id === fieldId)
    : documents;

  const validateFile = (file: File): string | null => {
    const extension = getFileExtension(file.name);
    if (!ALLOWED_EXTENSIONS.includes(extension)) {
      return `File type .${extension} is not allowed. Allowed types: ${ALLOWED_EXTENSIONS.join(', ')}`;
    }
    if (file.size > MAX_FILE_SIZE) {
      return `File size exceeds the maximum limit of ${formatFileSize(MAX_FILE_SIZE)}`;
    }
    return null;
  };

  const handleFileSelect = (file: File) => {
    const error = validateFile(file);
    if (error) {
      toast({
        title: 'Invalid File',
        description: error,
        variant: 'destructive',
      });
      return;
    }
    setSelectedFile(file);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  }, []);

  const handleUpload = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Simulate progress for better UX (actual progress would require XHR)
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 100);

      await onUpload(selectedFile, documentType, description, fieldId);
      
      clearInterval(progressInterval);
      setUploadProgress(100);

      toast({
        title: 'Upload Successful',
        description: `${selectedFile.name} has been uploaded successfully.`,
      });

      // Reset form
      setSelectedFile(null);
      setDescription('');
      setDocumentType(suggestedDocumentType || 'other');
    } catch (error) {
      showErrorToast(error, 'Upload Failed');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDownload = async (doc: AMLFormDocument) => {
    setDownloadingId(doc.id);
    try {
      await onDownload(doc);
    } catch (error) {
      showErrorToast(error, 'Download Failed');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDocument) return;

    setDeletingId(deleteDocument.id);
    try {
      await onDelete(deleteDocument);
      toast({
        title: 'Document Deleted',
        description: `${deleteDocument.original_filename} has been deleted.`,
      });
    } catch (error) {
      showErrorToast(error, 'Delete Failed');
    } finally {
      setDeletingId(null);
      setDeleteDocument(null);
    }
  };

  const cancelSelection = () => {
    setSelectedFile(null);
    setDescription('');
  };

  return (
    <div className="space-y-4">
      {/* Upload Area */}
      {!readOnly && (
        <div className="space-y-3">
          {!selectedFile ? (
            <div
              className={`
                border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer
                ${isDragging ? 'border-primary bg-primary/5' : 'border-gray-300 hover:border-gray-400'}
                ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
              `}
              onDragOver={!disabled ? handleDragOver : undefined}
              onDragLeave={!disabled ? handleDragLeave : undefined}
              onDrop={!disabled ? handleDrop : undefined}
              onClick={() => !disabled && fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={handleFileInputChange}
                disabled={disabled}
                accept={ALLOWED_EXTENSIONS.map(ext => `.${ext}`).join(',')}
              />
              <Upload className={`w-8 h-8 mx-auto mb-2 ${isDragging ? 'text-primary' : 'text-gray-400'}`} />
              <p className="text-sm font-medium text-foreground">
                {isDragging ? 'Drop file here' : 'Click or drag file to upload'}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Max {formatFileSize(MAX_FILE_SIZE)} • PDF, DOC, XLS, Images, and more
              </p>
            </div>
          ) : (
            <div className="border rounded-lg p-4 bg-muted/50 space-y-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  {getFileIcon(selectedFile.type)}
                  <div>
                    <p className="text-sm font-medium text-foreground truncate max-w-xs">
                      {selectedFile.name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatFileSize(selectedFile.size)}
                    </p>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={cancelSelection}
                  disabled={isUploading}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Document Type</Label>
                  <Select
                    value={documentType}
                    onValueChange={(value) => setDocumentType(value as DocumentType)}
                    disabled={isUploading}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(DOCUMENT_TYPE_LABELS).map(([value, label]) => (
                        <SelectItem key={value} value={value}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Description (Optional)</Label>
                  <Input
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Brief description..."
                    className="h-9"
                    disabled={isUploading}
                  />
                </div>
              </div>

              {isUploading && (
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div
                    className="bg-primary h-1.5 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              )}

              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={cancelSelection}
                  disabled={isUploading}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={handleUpload}
                  disabled={isUploading}
                  className="flex-1"
                >
                  {isUploading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Upload
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Documents Table */}
      {filteredDocuments.length > 0 && (
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="w-10"></TableHead>
                <TableHead>File Name</TableHead>
                <TableHead className={compact ? 'hidden sm:table-cell' : ''}>Type</TableHead>
                <TableHead className={compact ? 'hidden md:table-cell' : ''}>Size</TableHead>
                <TableHead className={compact ? 'hidden lg:table-cell' : ''}>Uploaded</TableHead>
                <TableHead className="text-right w-24">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredDocuments.map((doc) => (
                <TableRow key={doc.id}>
                  <TableCell className="py-2">
                    {getFileIcon(doc.mime_type)}
                  </TableCell>
                  <TableCell className="py-2">
                    <div className="flex flex-col">
                      <span className="font-medium text-sm truncate max-w-xs" title={doc.original_filename}>
                        {doc.original_filename}
                      </span>
                      {doc.description && (
                        <span className="text-xs text-muted-foreground truncate max-w-xs">
                          {doc.description}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className={`py-2 ${compact ? 'hidden sm:table-cell' : ''}`}>
                    <Badge 
                      variant="outline" 
                      className={DOCUMENT_TYPE_COLORS[doc.document_type]}
                    >
                      {DOCUMENT_TYPE_LABELS[doc.document_type]}
                    </Badge>
                  </TableCell>
                  <TableCell className={`py-2 text-sm text-muted-foreground ${compact ? 'hidden md:table-cell' : ''}`}>
                    {formatFileSize(doc.file_size)}
                  </TableCell>
                  <TableCell className={`py-2 text-sm text-muted-foreground ${compact ? 'hidden lg:table-cell' : ''}`}>
                    {new Date(doc.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="py-2">
                    <div className="flex items-center justify-end gap-1">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDownload(doc)}
                              disabled={downloadingId === doc.id}
                            >
                              {downloadingId === doc.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Download className="w-4 h-4" />
                              )}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Download</TooltipContent>
                        </Tooltip>

                        {!readOnly && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => setDeleteDocument(doc)}
                                disabled={deletingId === doc.id}
                              >
                                {deletingId === doc.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Trash2 className="w-4 h-4 text-destructive" />
                                )}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Delete</TooltipContent>
                          </Tooltip>
                        )}
                      </TooltipProvider>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Empty State */}
      {filteredDocuments.length === 0 && readOnly && (
        <div className="text-center py-6 text-muted-foreground border rounded-lg bg-muted/50">
          <File className="w-8 h-8 mx-auto mb-2 text-gray-400" />
          <p className="text-sm">No documents uploaded</p>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteDocument} onOpenChange={() => setDeleteDocument(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Document</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteDocument?.original_filename}"? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export { DOCUMENT_TYPE_LABELS, DOCUMENT_TYPE_COLORS };
